import { format, isValid, parse } from 'date-fns';
import { ManageSupplierStatus } from 'src/types/manage-suppliers/ManageSupplier';
import { SupplierFilterItem } from 'src/types/manage-suppliers/SupplierFilter';
import { SupplierEmailValidationStatus, SupplierTransmissionType } from 'src/types/manage-suppliers/SupplierForm';

/** Backend `PoTransmissionMethod`: Email = 1, LogOnly = 3, Inactive = -1 */
export const PO_TRANSMIT_EMAIL = 1;
export const PO_TRANSMIT_LOG_ONLY = 3;
export const PO_TRANSMIT_INACTIVE = -1;

// fax and offline
export const PO_TRANSMIT_OFFLINE = 3;

/** Backend `SupplierStatus`: Inactive = 0, Active = 1, Archived = 2 */
export const SUPPLIER_STATUS_INACTIVE = 0;
export const SUPPLIER_STATUS_ACTIVE = 1;
export const SUPPLIER_STATUS_ARCHIVED = 2;

/** Filter/list `validationStatus` values (may differ from upsert payload). */
export const SUPPLIER_VALIDATION_NONE = 0;
export const SUPPLIER_VALIDATION_PENDING = 1;
export const SUPPLIER_VALIDATION_VALIDATED = 2;
export const SUPPLIER_VALIDATION_FAILED = 3;

/** Upsert `validationStatus` values per UpsertFE.md */
export const UPSERT_VALIDATION_PENDING = 1;
export const UPSERT_VALIDATION_SUCCESSFUL = 2;
export const UPSERT_VALIDATION_FAILURE = -1;

export const toUpsertValidationStatus = (
  validationStatus: number | null | undefined,
  transmissionType: SupplierTransmissionType,
  isValidation = false
): number | null => {
  if (transmissionType !== 'email') {
    return null;
  }

  if (validationStatus === SUPPLIER_VALIDATION_VALIDATED || validationStatus === UPSERT_VALIDATION_SUCCESSFUL) {
    return UPSERT_VALIDATION_SUCCESSFUL;
  }

  if (validationStatus === SUPPLIER_VALIDATION_FAILED || validationStatus === UPSERT_VALIDATION_FAILURE) {
    return UPSERT_VALIDATION_FAILURE;
  }

  if (validationStatus === SUPPLIER_VALIDATION_PENDING || validationStatus === UPSERT_VALIDATION_PENDING) {
    return UPSERT_VALIDATION_PENDING;
  }

  if (isValidation) {
    return UPSERT_VALIDATION_PENDING;
  }

  return null;
};

export const toTransmissionType = (poTransmitMethod: number): SupplierTransmissionType =>
  poTransmitMethod === PO_TRANSMIT_EMAIL ? 'email' : 'logOnly';

type SupplierTransmissionContext = Pick<
  SupplierFilterItem,
  'poTransmitMethod' | 'validationStatus' | 'isValidation'
> & {
  email?: string | null;
};

/** Inactive suppliers may report poTransmitMethod=-1 while retaining email validation state. */
export const resolveSupplierTransmissionType = (
  item: SupplierTransmissionContext
): SupplierTransmissionType => {
  if (item.poTransmitMethod === PO_TRANSMIT_EMAIL) {
    return 'email';
  }

  const hasEmail = Boolean(item.email?.trim());
  if (hasEmail && toEmailValidationStatus(item) !== 'none') {
    return 'email';
  }

  return 'logOnly';
};

export const toApiPoTransmitMethod = (transmissionType: SupplierTransmissionType): number =>
  transmissionType === 'email' ? PO_TRANSMIT_EMAIL : PO_TRANSMIT_LOG_ONLY;

export const toApiSupplierStatus = (status: ManageSupplierStatus): number => {
  switch (status) {
    case 'inactive':
      return SUPPLIER_STATUS_INACTIVE;
    case 'archived':
      return SUPPLIER_STATUS_ARCHIVED;
    default:
      return SUPPLIER_STATUS_ACTIVE;
  }
};

export const toEmailValidationStatus = (
  item: Pick<SupplierFilterItem, 'validationStatus' | 'isValidation'>
): SupplierEmailValidationStatus => {
  if (item.validationStatus === SUPPLIER_VALIDATION_VALIDATED) {
    return 'validated';
  }

  if (
    item.validationStatus === SUPPLIER_VALIDATION_FAILED ||
    item.validationStatus === UPSERT_VALIDATION_FAILURE
  ) {
    return 'failed';
  }

  if (item.validationStatus === SUPPLIER_VALIDATION_PENDING || item.isValidation) {
    return 'pending';
  }

  return 'none';
};

export const isValidationExpired = (
  item: Pick<SupplierFilterItem, 'validationStatus'> & {
    validationErrorMessage?: string;
  }
): boolean => {
  if (
    item.validationStatus !== SUPPLIER_VALIDATION_FAILED &&
    item.validationStatus !== UPSERT_VALIDATION_FAILURE
  ) {
    return false;
  }

  return item.validationErrorMessage?.toLowerCase().includes('expir') ?? false;
};

/** Formats BE validation datetime. Backend already sends PST wall-clock — no TZ conversion. */
export const formatSupplierValidationDate = (date?: string): string | undefined => {
  if (!date) {
    return undefined;
  }

  // Drop ms / Z / offset so we format the PST clock face as-is.
  const normalized = date.trim().replace(/\.\d+/, '').replace(/(Z|[+-]\d{2}:?\d{2})$/, '');
  const parsed = parse(normalized, "yyyy-MM-dd'T'HH:mm:ss", new Date());

  if (!isValid(parsed)) {
    return undefined;
  }

  return `${format(parsed, 'M/d/yyyy h:mm:ss a')} (PT)`;
};

/** Formats the current moment in Pacific Time for optimistic validation UI (client-side only). */
export const formatCurrentSupplierValidationDate = (): string => {
  const formatted = new Date()
    .toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
    .replace(', ', ' ');

  return `${formatted} (PT)`;
};

/** Same company when the offline supplier is not shared across companies. */
export const isSameCompanySupplier = (item: Pick<SupplierFilterItem, 'isSharedOfflineSupplier'>) =>
  !item.isSharedOfflineSupplier;
